import React from 'react';

/**
 * MetricsCards
 *
 * Summary metric cards for the shipment operations monitor.
 * Each card shows a count with colour coding and a label.
 * Clicking a card fires onFilter() to set the active filter.
 *
 * @param {Object} counts   - { processing, retrying, failed, completed, stuck, slaBreach }
 * @param {string} activeFilter - currently active filter key
 * @param {Function} onFilter  - (filterKey) => void
 * @param {Object} strings     - strings.adminMonitor from i18n
 */
export default function MetricsCards({ counts, activeFilter, onFilter, strings: s = {} }) {
  const cards = [
    {
      key: 'processing',
      label: s.metricProcessing || 'Processing',
      count: counts.processing,
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-800',
      countColor: 'text-yellow-700',
      icon: '⏳',
    },
    {
      key: 'failed_reconciling',
      label: s.metricRetrying || 'Retrying',
      count: counts.retrying,
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-800',
      countColor: 'text-orange-700',
      icon: '🔄',
    },
    {
      key: 'permanent_failed',
      label: s.metricFailed || 'Permanent Failed',
      count: counts.failed,
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      countColor: 'text-red-700',
      icon: '🆘',
    },
    {
      key: 'completed',
      label: s.metricCompleted || 'Shipped Today',
      count: counts.completed,
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      countColor: 'text-green-700',
      icon: '✅',
    },
    {
      key: 'stuck',
      label: s.metricStuck || 'Stuck (>10 min)',
      count: counts.stuck,
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      countColor: 'text-red-700',
      icon: '🚨',
    },
    {
      key: 'sla_breach',
      label: s.metricSlaBreach || 'SLA Breached',
      count: counts.slaBreach,
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-800',
      countColor: 'text-orange-700',
      icon: '⚠️',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
      {cards.map((card) => {
        const isActive = activeFilter === card.key;
        return (
          <button
            key={card.key}
            onClick={() => onFilter(isActive ? 'all' : card.key)}
            className={`
              rounded-xl border p-4 text-left transition-all duration-200 w-full
              ${card.bg} ${card.border} ${card.text}
              ${isActive ? 'ring-2 ring-offset-1 ring-secondary shadow-card' : 'hover:shadow-md'}
            `}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-lg">{card.icon}</span>
              {isActive && (
                <span className="text-xs font-sans bg-secondary text-primaryBg px-1.5 py-0.5 rounded-full">
                  active
                </span>
              )}
            </div>
            <div className={`text-2xl font-heading font-bold ${card.countColor}`}>
              {card.count ?? 0}
            </div>
            <div className="text-xs font-sans mt-1 leading-snug">
              {card.label}
            </div>
          </button>
        );
      })}
    </div>
  );
}
